import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PRICING_PLANS, createCheckoutSession } from '../services/stripe';
import { cn } from '../utils/cn';

// Plan hierarchy for determining upgrades
const PLAN_ORDER = ['free_trial', 'pro', 'advanced', 'enterprise'];

/**
 * ContactSalesButton - Sends automatic notification email before opening mailto
 */
function ContactSalesButton({ user, isAuthenticated }) {
  const [sending, setSending] = useState(false);

  const handleContactSales = async () => {
    // First, send automatic notification to sales
    if (isAuthenticated && user) {
      setSending(true);
      try {
        await fetch('/.netlify/functions/contact-sales', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            userEmail: user.email,
            timestamp: new Date().toISOString(),
          }),
        });
      } catch (error) {
        // Don't block the mailto if notification fails
        console.error('Contact sales notification failed:', error);
      } finally {
        setSending(false);
      }
    }

    // Then open mailto for user to compose their message
    window.location.href = `mailto:sales@askaiq.com?subject=AskAIQ%20Premium%20Inquiry${isAuthenticated ? `&body=User%20ID:%20${user.id}%0AEmail:%20${user.email}%0A%0A` : ''}`;
  };

  return (
    <button
      onClick={handleContactSales}
      disabled={sending}
      className={cn(
        "w-full py-3 rounded-lg font-medium",
        "border border-border hover:bg-muted",
        "transition-all flex items-center justify-center gap-2",
        "disabled:opacity-50"
      )}
    >
      {sending ? (
        <>
          <Loader2 className="w-5 h-5 animate-spin" />
          Opening...
        </>
      ) : (
        <>
          <Mail className="w-5 h-5" />
          Contact Sales
        </>
      )}
    </button>
  );
}

export default function PricingPage() {
  const { user, profile, isAuthenticated } = useAuth();
  const currentTier = profile?.subscription_tier || 'free_trial';

  // Check if a plan is an upgrade from current
  const isUpgrade = (planTier) => {
    const currentIndex = PLAN_ORDER.indexOf(currentTier);
    const planIndex = PLAN_ORDER.indexOf(planTier);
    return planIndex > currentIndex;
  };
  const [loading, setLoading] = useState(null);

  const handleSubscribe = async (plan) => {
    if (!isAuthenticated) {
      window.location.href = '/register';
      return;
    }

    setLoading(plan.id);
    try {
      await createCheckoutSession(plan.priceId, user.id, user.email);
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <Link to="/" className="text-3xl font-display font-bold text-primary">
            AskAIQ
          </Link>
          <h1 className="text-display font-display text-primary mt-8 mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-subtitle text-muted-foreground max-w-2xl mx-auto">
            Start with 3 free research requests. Upgrade when you need more.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {/* Free Trial */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={cn(
              "bg-card rounded-xl shadow-card p-8 border relative",
              currentTier === 'free_trial' ? "border-2 border-secondary" : "border-border"
            )}
          >
            {currentTier === 'free_trial' && isAuthenticated && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Current Plan
              </div>
            )}
            <h3 className="text-xl font-display font-bold text-primary mb-2">Free Trial</h3>
            <p className="text-muted-foreground mb-4">Perfect for trying out</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-primary">$0</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                3 research requests (one-time)
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Full market analysis
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                PDF exports
              </li>
            </ul>
            {!isAuthenticated && (
              <Link
                to="/register"
                className="block w-full py-3 rounded-lg font-medium text-center border border-border hover:bg-muted transition-all"
              >
                Get Started Free
              </Link>
            )}
          </motion.div>

          {/* Pro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={cn(
              "bg-card rounded-xl shadow-card p-8 relative",
              currentTier === 'pro' ? "border-2 border-secondary" : "border border-border"
            )}
          >
            {currentTier === 'pro' && isAuthenticated ? (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Current Plan
              </div>
            ) : currentTier === 'free_trial' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-full">
                Popular
              </div>
            )}
            <h3 className="text-xl font-display font-bold text-primary mb-2">Pro</h3>
            <p className="text-muted-foreground mb-4">For regular research needs</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-primary">${PRICING_PLANS.pro.price}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                {PRICING_PLANS.pro.requests} requests per month
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Full market analysis
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                PDF exports
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Priority support
              </li>
            </ul>
            {isUpgrade('pro') ? (
              <button
                onClick={() => handleSubscribe(PRICING_PLANS.pro)}
                disabled={loading === 'pro'}
                className={cn(
                  "w-full py-3 rounded-lg font-medium",
                  "bg-secondary text-secondary-foreground",
                  "hover:bg-secondary/90 disabled:opacity-50",
                  "transition-all flex items-center justify-center gap-2"
                )}
              >
                {loading === 'pro' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Subscribe to Pro'
                )}
              </button>
            ) : currentTier === 'pro' ? (
              <Link
                to="/account"
                className="block w-full py-3 rounded-lg font-medium text-center border border-border hover:bg-muted transition-all"
              >
                Manage Subscription
              </Link>
            ) : null}
          </motion.div>

          {/* Advanced */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={cn(
              "bg-card rounded-xl shadow-card p-8 relative",
              currentTier === 'advanced' ? "border-2 border-secondary" : "border border-border"
            )}
          >
            {currentTier === 'advanced' && isAuthenticated && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Current Plan
              </div>
            )}
            <h3 className="text-xl font-display font-bold text-primary mb-2">Advanced</h3>
            <p className="text-muted-foreground mb-4">For power users</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-primary">${PRICING_PLANS.advanced.price}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                {PRICING_PLANS.advanced.requests} requests per month
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Full market analysis
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                PDF exports
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Priority support
              </li>
            </ul>
            {isUpgrade('advanced') ? (
              <button
                onClick={() => handleSubscribe(PRICING_PLANS.advanced)}
                disabled={loading === 'advanced'}
                className={cn(
                  "w-full py-3 rounded-lg font-medium",
                  currentTier === 'free_trial'
                    ? "border border-border hover:bg-muted"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/90",
                  "disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                )}
              >
                {loading === 'advanced' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : currentTier === 'pro' ? (
                  'Upgrade to Advanced'
                ) : (
                  'Subscribe to Advanced'
                )}
              </button>
            ) : currentTier === 'advanced' ? (
              <Link
                to="/account"
                className="block w-full py-3 rounded-lg font-medium text-center border border-border hover:bg-muted transition-all"
              >
                Manage Subscription
              </Link>
            ) : null}
          </motion.div>

          {/* Premium */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className={cn(
              "bg-card rounded-xl shadow-card p-8 relative",
              currentTier === 'enterprise' ? "border-2 border-secondary" : "border border-border"
            )}
          >
            {currentTier === 'enterprise' && isAuthenticated && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Current Plan
              </div>
            )}
            <h3 className="text-xl font-display font-bold text-primary mb-2">Premium</h3>
            <p className="text-muted-foreground mb-4">Enterprise solutions</p>
            <div className="mb-6">
              <span className="text-4xl font-bold text-primary">Custom</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Analyst-validated data
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Custom research services
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Dedicated support
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="w-5 h-5 text-success" />
                Custom integrations
              </li>
            </ul>
            {currentTier === 'enterprise' ? (
              <Link
                to="/account"
                className="block w-full py-3 rounded-lg font-medium text-center border border-border hover:bg-muted transition-all"
              >
                Manage Subscription
              </Link>
            ) : (
              <ContactSalesButton user={user} isAuthenticated={isAuthenticated} />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
